/**
 * Dashboard Utility Functions
 * UI-level calculations for engagement analytics
 */

import type { MonthlyData } from '@/types/ui/dashboard'
import { parseDateOnly } from '@/lib/utils'

export type TimePeriod = 'monthly' | 'quarterly' | 'yearly'

export interface AggregatedData {
  period: string // Display label: "Jan" ("Jan 2024" when the span crosses years), "Q1 2024", "2024"
  periodDate: string // ISO date for sorting
  revenue: number
  costs: number
  billed: number
  grossProfit: number
}

/**
 * Get quarter label like "Q1 2024"
 */
function getQuarterLabel(year: number, quarter: number): string {
  return `Q${quarter} ${year}`
}

/**
 * Format a period's ISO date for exports as MM-DD-YYYY.
 * For quarterly/yearly aggregation periodDate is already the period start date, so the
 * same formatter yields "04-01-2025" / "01-01-2025" with no special casing.
 * Uses parseDateOnly so the YYYY-MM-DD string is read as a local date rather than UTC
 * midnight, which would shift the month in negative-UTC zones.
 * @param periodDate ISO "YYYY-MM-DD" date
 * @returns "MM-DD-YYYY", or an empty string when the date cannot be parsed
 */
export function formatPeriodForExport(periodDate: string | null | undefined): string {
  const date = parseDateOnly(periodDate)
  if (!date) return ''
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${month}-${day}-${date.getFullYear()}`
}

/**
 * Aggregate monthly data by time period (monthly/quarterly/yearly)
 * @param monthlyData Array of monthly financial data
 * @param timePeriod Time period to aggregate by
 * @returns Aggregated data matching the selected time period
 */
export function aggregateDataByTimePeriod(
  monthlyData: MonthlyData[],
  timePeriod: TimePeriod
): AggregatedData[] {
  // Group data by period key. The display label is stored alongside rather than
  // used as the key: the monthly label is a bare short month ("Apr"), so keying
  // on it merges April 2024 with April 2025 on an engagement spanning a year.
  const groups = new Map<string, {
    label: string
    periodDate: string
    revenue: number
    costs: number
    billed: number
    grossProfit: number
  }>()

  monthlyData.forEach(item => {
    // Parse the YYYY-MM-DD monthDate as a local date — new Date(item.monthDate)
    // would treat it as UTC midnight and read year/month in local time, bucketing
    // Jan 2025 under Q4 2024 / year 2024 in negative-UTC zones.
    const date = parseDateOnly(item.monthDate)
    if (!date) return
    const year = date.getFullYear()
    const month = date.getMonth() // 0-indexed

    // Determine key and periodDate based on time period. periodDate is built from
    // string parts rather than via new Date(...).toISOString() to avoid the
    // opposite UTC shift in positive-UTC zones.
    let key: string
    let label: string
    let periodDate: string

    if (timePeriod === 'monthly') {
      // Key on the ISO month, not the short label, which collides across years.
      key = item.monthDate
      label = item.month
      periodDate = item.monthDate
    } else if (timePeriod === 'quarterly') {
      const quarter = Math.floor(month / 3) + 1
      label = getQuarterLabel(year, quarter)
      key = label
      const quarterStartMonth = (quarter - 1) * 3 // 0-indexed: 0, 3, 6, 9
      periodDate = `${year}-${String(quarterStartMonth + 1).padStart(2, '0')}-01`
    } else { // yearly
      label = year.toString()
      key = label
      periodDate = `${year}-01-01`
    }

    // Initialize group if it doesn't exist
    if (!groups.has(key)) {
      groups.set(key, {
        label,
        periodDate,
        revenue: 0,
        costs: 0,
        billed: 0,
        grossProfit: 0
      })
    }

    // Aggregate values
    const group = groups.get(key)!
    group.revenue += item.revenue
    group.costs += item.costs
    group.billed += (item.billed || 0)
    group.grossProfit += (item.grossProfit || 0)
  })

  // Convert to array and sort by date
  const rows = Array.from(groups.values())

  // A monthly label is a bare "Apr". Qualify it with the year when the engagement
  // crosses a year boundary, otherwise two different Aprils share one tick. The
  // span is only known once every row is grouped, hence the check out here.
  // Quarterly and yearly labels already carry the year.
  const spansMultipleYears =
    timePeriod === 'monthly' &&
    new Set(rows.map(r => r.periodDate.slice(0, 4))).size > 1

  return rows
    .map(({ label, ...data }) => ({
      period: spansMultipleYears ? `${label} ${data.periodDate.slice(0, 4)}` : label,
      ...data
    }))
    .sort((a, b) => a.periodDate.localeCompare(b.periodDate))
}

/**
 * Calculates month-over-month revenue trend
 */
export function calculateRevenueTrend(monthlyData: MonthlyData[]): number | null {
  if (monthlyData.length < 2) return null
  
  const currentMonth = monthlyData[monthlyData.length - 1]
  const previousMonth = monthlyData[monthlyData.length - 2]
  
  if (previousMonth.revenue === 0) return null
  
  return ((currentMonth.revenue - previousMonth.revenue) / previousMonth.revenue) * 100
}
